import React, { useCallback, useMemo, useState } from 'react';
import { useDebounce } from '@toss/react';
import cns from 'classnames';
import { endOfDay, isAfter, isBefore, startOfDay } from 'date-fns';
import { shuffle } from 'lodash-es';
import hash from 'object-hash';
import { Carousel, useCarousel } from '@zep/web-components';
import { useBanners } from '../../../../services';
import { ChevronLeftIcon, ChevronRightIcon } from '../../../icons';
import { Banner } from './Banner';
import { BannerSchema, BannerSchemaType } from './BannerSchema';
import S from './BannerSlider.module.scss';
const MOBILE_BANNER_BREAKPOINT = 600;
const MOBILE_BANNER_WIDTH = 316;
const MOBILE_BANNER_MIN_WIDTH = 256;
const MOBILE_BANNER_SIDE_PREVIEW_WIDTH = 32;
const VISIBLE_SLIDES_COUNT = 1;
export const BannerSlider = (props: BannerSliderProps<BannerSchemaType>) => {
  const {
    bannerList
  } = props;
  const [currentSlide, setCurrentSlide] = useState(0);
  const [sliderCreated, setSliderCreated] = useState(false);
  const [indicatorText, setIndicatorText] = useState('');
  const [containerRef, sliderRef] = useCarousel({
    initial: 0,
    loop: true,
    slideChanged(slider) {
      setCurrentSlide(slider.track.details.rel);
      updateIndicatorText();
    },
    created(slider) {
      setSliderCreated(true);
      setCurrentSlide(slider.track.details.rel);
      updateIndicatorText();
    },
    slides(containerWidth: number, elems: HTMLElement[]) {
      const isMobile = typeof window !== 'undefined' && window.innerWidth < MOBILE_BANNER_BREAKPOINT;
      const spacing = isMobile ? 16 : 20;
      const safeContainerWidth = Math.max(containerWidth, 1);
      const mobileSlideWidth = Math.min(MOBILE_BANNER_WIDTH, safeContainerWidth, Math.max(MOBILE_BANNER_MIN_WIDTH, safeContainerWidth - MOBILE_BANNER_SIDE_PREVIEW_WIDTH * 2));
      const mobileSlideSize = mobileSlideWidth / safeContainerWidth;
      const config = {
        origin: isMobile ? (1 - mobileSlideSize) / 2 : 0,
        size: isMobile ? mobileSlideSize : 1,
        spacing: spacing / safeContainerWidth
      };
      return new Array(elems.length).fill(config);
    }
  }, [Carousel.AutoSlidePlugin()]);
  const updateIndicatorText = useDebounce(() => {
    setIndicatorText(`${currentSlide + 1}/${safeBannerList.length}`);
  }, 150);
  const add = useCallback((amount: number) => {
    const slider = sliderRef.current;
    if (!slider) {
      return;
    }
    slider.moveToIdx(slider.track.details.abs + amount, true);
  }, [sliderRef]);
  const focus = useCallback((index: number) => {
    const slider = sliderRef.current;
    if (!slider) {
      return;
    }
    const slidesLength = slider.slides.length;
    let distance = index - slider.track.details.rel;
    if (distance > slidesLength / 2) {
      distance -= slidesLength;
    } else if (distance < -slidesLength / 2) {
      distance += slidesLength;
    }
    if (distance >= VISIBLE_SLIDES_COUNT) {
      distance -= VISIBLE_SLIDES_COUNT - 1;
    }
    slider.moveToIdx(slider.track.details.abs + distance, true);
  }, [sliderRef]);
  const visibleSlideIndexes = useMemo(() => {
    const slider = sliderRef.current;
    if (!slider || !sliderCreated) {
      return new Set();
    }
    const indexes = new Set<number>([currentSlide]);
    for (let i = 0; i < VISIBLE_SLIDES_COUNT; i++) {
      indexes.add(slider.track.absToRel(slider.track.details.abs + i));
    }
    return indexes;
  }, [sliderRef, sliderCreated, currentSlide]);
  const safeBannerList = useMemo(() => {
    return bannerList.reduce<BannerSchemaType[]>((acc, banner) => {
      try {
        const parsed = BannerSchema.parse(banner);
        acc.push(parsed);
        return acc;
      } catch (e) {
        console.error(e);
        return acc;
      }
    }, []);
  }, [bannerList]);
  return <section className={S.slider_wrapper} data-sentry-component="BannerSlider" data-sentry-source-file="BannerSlider.tsx">
      {!!safeBannerList?.length && <Carousel ref={containerRef} className={cns(S.carousel, {
      [S.ready]: sliderCreated
    })}>
          {safeBannerList.map((banner, index) => <Banner key={banner.id} banner={banner} isVisible={visibleSlideIndexes.has(index)} onRequestFocus={() => focus(index)} />)}
        </Carousel>}
      {safeBannerList?.length && sliderCreated && <div className={S.controller}>
          <div className={S.controller_button}>
            <ChevronLeftIcon onClick={() => add(-1)} />
          </div>
          <div className={S.controller_indicator}>
            <span className={S.controller_indicator_text}>{indicatorText}</span>
          </div>
          <div className={S.controller_button}>
            <ChevronRightIcon onClick={() => add(1)} />
          </div>
        </div>}
    </section>;
};
export const Banners = () => {
  const {
    data: banners
  } = useBanners<BannerSchemaType[]>({
    refetchOnWindowFocus: false
  });
  const activeBanners = useMemo(() => {
    if (!banners) {
      return [];
    }
    const today = new Date();
    return shuffle(banners.filter((b: BannerSchemaType) => (b.start_date ? isAfter(today, startOfDay(new Date(b.start_date))) : true) && (b.end_date ? isBefore(today, endOfDay(new Date(b.end_date))) : true)));
  }, [banners]);
  return <BannerSlider bannerList={activeBanners} key={hash(activeBanners)} data-sentry-element="BannerSlider" data-sentry-component="Banners" data-sentry-source-file="BannerSlider.tsx" />;
};
type BannerSliderProps<T> = {
  bannerList: T[];
};